import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { Star } from "./brand/Logo";
import { Reveal } from "./Motion";
import { useLang } from "../i18n/LangContext";

const STYLES = {
  essential: { card: "bg-transparent border border-ink/15 text-ink", btn: "border border-ink text-ink hover:bg-ink hover:text-ivory", check: "text-lav-deep" },
  professional: { card: "bg-lav-soft border border-lav text-ink lg:-translate-y-5 shadow-[0_30px_80px_-40px_rgba(122,92,196,0.6)]", btn: "bg-ink text-ivory hover:bg-lav-deep", check: "text-lav-deep" },
  premium: { card: "bg-ink border border-ink text-ivory", btn: "border border-lav text-ivory hover:bg-lav hover:text-ink", check: "text-lav" },
};

const PlanCard = ({ plan, i }) => {
  const { t, path } = useLang();
  const s = STYLES[plan.key];
  return (
    <Reveal delay={i * 0.08} className="h-full">
      <article className={`relative flex h-full flex-col rounded-sm p-8 transition-transform duration-500 md:p-10 ${s.card}`} data-testid={`plan-card-${plan.key}`}>
        {plan.key === "professional" && (
          <span className="label absolute -top-3 left-8 rounded-full bg-ink px-4 py-1.5 text-[10px] text-lav" data-testid="plan-recommended-badge">
            {t.pricing.recommended}
          </span>
        )}
        <div className="flex items-center justify-between">
          <span className="label opacity-60">0{i + 1}</span>
          <Star className={`h-5 w-5 ${plan.key === "premium" ? "text-lav" : "text-ink"}`} />
        </div>
        <h3 className="mt-8 font-display text-3xl font-bold uppercase tracking-tight">{plan.name}</h3>
        <p className="mt-3 min-h-[3rem] text-sm leading-relaxed opacity-70">{plan.desc}</p>
        <p className="mt-8 font-display text-6xl font-bold tracking-tighter" data-testid={`plan-price-${plan.key}`}>{plan.price}</p>
        <div className="my-8 h-px bg-current opacity-15" />
        <p className="label mb-5 opacity-60">{t.pricing.includes}</p>
        <ul className="flex-1 space-y-3">
          {plan.items.map((it, j) => {
            const brandLine = plan.key === "premium" && j === 2;
            return (
              <li key={it} className={`flex gap-3 text-sm leading-snug ${brandLine ? "rounded-sm bg-lav/15 p-3 -mx-3 font-medium text-ivory" : ""}`}
                data-testid={brandLine ? "premium-branding-feature" : undefined}>
                {brandLine ? <Star className="mt-0.5 h-4 w-4 shrink-0 text-acid" /> : <Check className={`mt-0.5 h-4 w-4 shrink-0 ${s.check}`} />}
                {it}
              </li>
            );
          })}
        </ul>
        <Link to={`${path("contact")}?package=${plan.key}`} data-testid={`plan-choose-${plan.key}`}
          className={`label group mt-10 flex items-center justify-center gap-2 rounded-full px-6 py-4 transition-colors duration-300 ${s.btn}`}>
          {t.pricing.choose} {plan.name}
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </article>
    </Reveal>
  );
};

export const PricingGrid = () => {
  const { t } = useLang();
  return (
    <div>
      <div className="grid gap-8 pt-6 lg:grid-cols-3 lg:gap-6">
        {t.pricing.plans.map((p, i) => <PlanCard key={p.key} plan={p} i={i} />)}
      </div>
      <p className="mt-12 flex max-w-3xl gap-3 text-sm leading-relaxed text-ink/70" data-testid="pricing-scope-note">
        <Star className="mt-1 h-3.5 w-3.5 shrink-0 text-lav-deep" />{t.common.scopeNote}
      </p>
    </div>
  );
};

const TermList = ({ title, items, inc }) => (
  <div>
    <p className="label mb-6 text-mist">{title}</p>
    <ul className="space-y-4">
      {items.map((it) => (
        <li key={it} className={`flex items-start gap-3 text-base ${inc ? "text-ivory" : "text-ivory/50"}`}>
          {inc ? <Check className="mt-1 h-4 w-4 shrink-0 text-lav" /> : <Minus className="mt-1 h-4 w-4 shrink-0 text-mist" />}{it}
        </li>
      ))}
    </ul>
  </div>
);

export const Maintenance = () => {
  const { t, path } = useLang();
  const m = t.maint;
  return (
    <div className="relative overflow-hidden rounded-sm border border-white/10 bg-char p-8 md:p-14" data-testid="maintenance-section">
      <Star className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] text-lav/[0.07]" />
      <div className="relative grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="label text-lav">{m.label}</p>
          <h3 className="mt-5 font-display text-3xl font-bold uppercase leading-none tracking-tight text-ivory md:text-4xl">{m.name}</h3>
          <p className="mt-10 flex items-baseline gap-3">
            <span className="font-display text-7xl font-bold tracking-tighter text-ivory md:text-8xl" data-testid="maintenance-price">{m.price}</span>
            <span className="label text-mist">{m.per}</span>
          </p>
          <div className="mt-8 inline-flex -rotate-2 items-center gap-3 rounded-sm bg-acid px-5 py-3 text-ink" data-testid="maintenance-first-month-free">
            <Star className="h-4 w-4" />
            <span className="font-display text-lg font-extrabold uppercase tracking-tight">{m.free}</span>
          </div>
          <Link to={`${path("contact")}?package=maintenance`} data-testid="maintenance-cta"
            className="label group mt-10 flex w-fit items-center gap-2 rounded-full border border-lav px-6 py-4 text-ivory transition-colors duration-300 hover:bg-lav hover:text-ink">
            {m.cta}<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid gap-10 sm:grid-cols-2 lg:col-span-7">
          <TermList title={m.incTitle} items={m.inc} inc />
          <TermList title={m.excTitle} items={m.exc} />
          <div className="space-y-3 border-t border-white/10 pt-6 text-sm leading-relaxed text-mist sm:col-span-2" data-testid="maintenance-terms">
            <p className="text-ivory/80">{m.quoted}</p>
            <p>{m.domain}</p>
            <p>{m.hosting}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
